import { CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type MicrosoftIntegration } from "~/api/v2/useMicrosoftIntegration";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/atoms/accordion";
import { Alert, AlertDescription } from "~/atoms/alert";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";

const redirectUrl = `${window.location.origin}/api/v2/onboarding/microsoft/admin-consent`;

const constructAdminConsentUrl = (clientId: string, setupToken: string) =>
  `https://login.microsoftonline.com/organizations/adminconsent?client_id=${clientId}&redirect_uri=${encodeURI(redirectUrl)}&state=${setupToken}`;

interface Props {
  data: MicrosoftIntegration;
  setupToken: string;
}

export function AdminConsentCard({ data, setupToken }: Props) {
  const { t } = useTranslation();

  const handleGrantConsent = () => {
    window.location.assign(constructAdminConsentUrl(data.clientId, setupToken));
  };

  const isCompleted = data.state !== "pending_admin_consent";

  if (isCompleted) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {t(($) => $.msIntegration.steps.adminConsent.title)}
          </CardTitle>
          <CardDescription>
            {t(($) => $.msIntegration.steps.adminConsent.description)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <CheckCircle className="text-success size-4" />
            <AlertDescription>
              {t(($) => $.msIntegration.adminCheck.isAdmin)}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t(($) => $.msIntegration.steps.adminConsent.title)}
        </CardTitle>
        <CardDescription>
          <p>{t(($) => $.msIntegration.adminCheck.description)}</p>
          <br />
          <p>{t(($) => $.msIntegration.adminCheck.details)}</p>
          <br />
          <p>{t(($) => $.msIntegration.adminCheck.privacyNote)}</p>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm font-semibold">
            {t(($) => $.msIntegration.permissionsRequired)}
          </p>
          <p className="text-muted-foreground text-sm">
            {t(($) => $.msIntegration.permissionsDescription)}
          </p>
        </div>

        <Accordion type="multiple" className="text-foreground w-full">
          <AccordionItem value="sso">
            <AccordionTrigger className="text-xs">
              {t(($) => $.msIntegration.ssoPermissions)}
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground text-xs">
                {t(($) => $.msIntegration.ssoPermissionsDesc)}
              </p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="file-storage-delegated">
            <AccordionTrigger className="text-xs">
              {t(($) => $.msIntegration.fileStoragePermission)}
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground text-xs">
                {t(($) => $.msIntegration.fileStoragePermissionDesc)}
              </p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="container-type-reg-selected">
            <AccordionTrigger className="text-xs">
              {t(($) => $.msIntegration.containerTypeRegPermission)}
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground text-xs">
                {t(($) => $.msIntegration.containerTypeRegPermissionDesc)}
              </p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="sites-selected-application">
            <AccordionTrigger className="text-xs">
              {t(($) => $.msIntegration.sitesSelectedPermission)}
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground text-xs">
                {t(($) => $.msIntegration.sitesSelectedPermissionDesc)}
              </p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="user-read-delegated">
            <AccordionTrigger className="text-xs">
              {t(($) => $.msIntegration.userReadPermission)}
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground text-xs">
                {t(($) => $.msIntegration.userReadPermissionDesc)}
              </p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="sensitivity-labels-read">
            <AccordionTrigger className="text-xs">
              {t(($) => $.msIntegration.sensitivityLabelsPermission)}
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground text-xs">
                {t(($) => $.msIntegration.sensitivityLabelsPermissionDesc)}
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Button onClick={handleGrantConsent} className="w-full">
          {t(($) => $.msIntegration.adminCheck.button)}
        </Button>
      </CardContent>
    </Card>
  );
}
